import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import AdminSidebar from '../components/AdminSidebar';
import { authFetch } from '../services/authFetch';
import * as XLSX from 'xlsx';

const AdminLaporanKersosPage = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPermit, setSelectedPermit] = useState(null);
  const [user, setUser] = useState(null);
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [accordionState, setAccordionState] = useState({
    suratIjinOpen: false,
    laporanOpen: false,
    masterDataOpen: false
  });

  // Real data for overtime reports
  const [suratIjinRequests, setSuratIjinRequests] = useState([]);
  const [adminUsers, setAdminUsers] = useState([]);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);

  // Date formatting helper function
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '-';
      
      const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 
                     'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      
      const day = date.getDate();
      const month = months[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day} ${month} ${year}`;
    } catch (error) {
      return '-';
    }
  };

  // Always attempt to fetch data - the API will handle auth checks
  // This prevents issues where the user object might not be loaded yet

  // Pagination states
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchReports = async (searchFilter = searchQuery, dateFilterParam = dateFilter, statusFilterParam = statusFilter) => {
    try {
      setLoading(true);
      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
      
      // Build query parameters
      const params = new URLSearchParams();
      if (searchFilter) params.append('search', searchFilter);
      if (dateFilterParam) params.append('date', dateFilterParam);
      if (statusFilterParam) params.append('status', statusFilterParam);
      params.append('page', currentPage);
      params.append('per_page', itemsPerPage);
      
      const queryString = params.toString();
      const url = `${apiBase}/api/admin/kersos-reports${queryString ? `?${queryString}` : ''}`;
      
      // Use the imported authFetch instead of window.authFetch
      const res = await authFetch(url, {
        headers: { 'Accept': 'application/json' }
      });
      
      if (!res.ok) {
        const errorText = await res.text();
        console.error('API Error Response:', errorText);
        throw new Error(`Failed to fetch reports: ${res.status} ${errorText}`);
      }
      
      const data = await res.json();
      
      // Handle paginated response
      if (data.data) {
        // Laravel paginated response
        const sourceData = data.data || [];
        setTotalPages(data.last_page || 1);
        setTotalItems(data.total || 0);
        
        // Map backend data to UI format for kersos reports
        const mapped = sourceData.map(item => {
          return {
            id: item.id,
            nama: item.student?.name || 'N/A',
            student: item.student,
            kelas: item.student?.class || 'N/A',
            tanggalKersos: item.kersos_date || '',
            jamMulai: item.start_time || '',
            jamSelesai: item.finish_time || '',
            jenisPekerjaan: item.type_or_work || '',
            refleksi: item.reflection || '',
            pic: item.pic || '',
            nomorTelepon: item.pic_contact_number || '',
            status: item.request_status || 'menunggu',
            statusText: item.request_status === 'disetujui' ? 'Disetujui' : 
                       item.request_status === 'ditolak' ? 'Ditolak' : 'Menunggu',
            images: item.images || [],
            // Add decision_by fields
            decision_by: item.decision_by,
            decision_by_id: item.decision_by_id
          };
        });
        
        setSuratIjinRequests(mapped);
      } else {
        // Fallback for non-paginated response
        const sourceData = data || [];
        
        // Map backend data to UI format for kersos reports
        const mapped = sourceData.map(item => {
          return {
            id: item.id,
            nama: item.student?.name || 'N/A',
            student: item.student,
            kelas: item.student?.class || 'N/A',
            tanggalKersos: item.kersos_date || '',
            jamMulai: item.start_time || '',
            jamSelesai: item.finish_time || '',
            jenisPekerjaan: item.type_or_work || '',
            refleksi: item.reflection || '',
            pic: item.pic || '',
            nomorTelepon: item.pic_contact_number || '',
            status: item.request_status || 'menunggu',
            statusText: item.request_status === 'disetujui' ? 'Disetujui' : 
                       item.request_status === 'ditolak' ? 'Ditolak' : 'Menunggu',
            images: item.images || [],
            // Add decision_by fields
            decision_by: item.decision_by,
            decision_by_id: item.decision_by_id
          };
        });
        
        setSuratIjinRequests(mapped);
        setTotalItems(mapped.length);
        setTotalPages(Math.ceil(mapped.length / itemsPerPage));
      }
    } catch (e) {
      setNotification({
        show: true,
        message: `Error fetching data: ${e.message}`,
        type: 'error'
      });
      
      // Auto-hide error notification after 5 seconds
      setTimeout(() => {
        setNotification({ show: false, message: '', type: 'success' });
      }, 5000);
      
      setSuratIjinRequests([]);
      setTotalItems(0);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
    
    // Fetch admin users for decision maker info
    async function fetchAdminUsers() {
      try {
        const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
        const res = await authFetch(`${apiBase}/api/admin_users`, {
          headers: { 'Accept': 'application/json' }
        });
        if (res.ok) {
          const data = await res.json();
          setAdminUsers(data.data || data);
        }
      } catch (error) {
        console.error('Error fetching admin users:', error);
        setAdminUsers([]);
      }
    }
    fetchAdminUsers();
    
    // eslint-disable-next-line
  }, [searchQuery, dateFilter, statusFilter, currentPage, itemsPerPage]);

  // Since filtering is now handled by the backend, we use the data directly
  const currentPageItems = suratIjinRequests;
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);

  // Reset current page when search query changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  // Fetch user data from localStorage for sidebar
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Helper function to get admin name
  const getDecisionMakerName = (permit) => {
    if (!permit) {
      return 'Admin';
    }

    // Ideal case: The backend sends the related decision_by object with the name.
    if (permit.decision_by && permit.decision_by.name) {
      return permit.decision_by.name;
    }

    // Fallback: If only decision_by_id is available, look up the name in adminUsers list.
    if (permit.decision_by_id && adminUsers.length > 0) {
      // Use '==' for loose comparison to avoid issues with string vs. number IDs.
      const admin = adminUsers.find(admin => admin.id == permit.decision_by_id);
      return admin ? admin.name : 'Admin';
    }
    
    return 'Admin';
  };

  // Normalize avatar URL
  const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000/').replace(/\/$/, '');
  const avatarUrl = user?.avatar_url
    ? user.avatar_url.startsWith('http')
      ? user.avatar_url
      : `${baseUrl}${user.avatar_url.startsWith('/') ? '' : '/'}${user.avatar_url}`
    : '';

  const adminData = {
    name: user?.name || '',
    position: user?.role || '',
    avatarUrl: avatarUrl,
  };

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ ...notification, show: false });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification]);

  const handleStudentClick = (student) => {
    if (student) {
      navigate('/admin/status-siswa', { state: { student: student } });
    } else {
      setNotification({
        show: true,
        message: 'Data siswa tidak ditemukan atau tidak lengkap.',
        type: 'error',
      });
    }
  };

  // Excel export function
  const handleExcelExport = async () => {
    try {
      setNotification({
        show: true,
        message: 'Memproses ekspor Excel...',
        type: 'success'
      });

      // Check if user is authenticated before making request
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('No authentication token found. Please login again.');
      }

      // Build query parameters based on current filters
      const params = new URLSearchParams();
      if (searchQuery) params.append('search', searchQuery);
      if (dateFilter) params.append('date', dateFilter);
      if (statusFilter) params.append('status', statusFilter);

      console.log('Making Excel export request to:', `/api/admin/kersos-reports/export-excel?${params.toString()}`);
      console.log('Authentication token present:', !!token);

      const response = await authFetch(`/api/admin/kersos-reports/export-excel?${params.toString()}`, {
        method: 'PUT',
        autoLogoutOn401: false
      });
      
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));

      if (!response.ok) {
        // Get more detailed error information
        let errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.text();
          console.log('Error response body:', errorData);
          if (errorData) {
            errorMessage += ` - ${errorData}`;
          }
        } catch (e) {
          console.log('Could not read error response body');
        }
        throw new Error(errorMessage);
      }

      const data = await response.json();
      console.log('Export data received:', data);
      
      // Validate data structure
      if (!data || !data.data || !Array.isArray(data.data)) {
        throw new Error('Invalid data format received from server');
      }

      if (data.data.length === 0) {
        setNotification({
          show: true,
          message: 'Tidak ada data untuk diekspor dengan filter yang dipilih',
          type: 'error'
        });
        return;
      }
      
      // Create Excel workbook
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.json_to_sheet(data.data);
      
      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Laporan Kersos');
      
      // Generate filename with current date
      const currentDate = new Date().toLocaleDateString('id-ID').replace(/\//g, '_');
      const filename = `Laporan_Kersos_${currentDate}.xlsx`;
      
      // Download the file
      XLSX.writeFile(workbook, filename);
      
      setNotification({
        show: true,
        message: `File Excel berhasil diunduh: ${filename}`,
        type: 'success'
      });
      
    } catch (error) {
      console.error('Error exporting Excel:', error);
      setNotification({
        show: true,
        message: `Gagal mengekspor Excel: ${error.message}`,
        type: 'error'
      });
    }
  };

  return (
    <PageContainer>
      
      <PageHeader>
        <HeaderContent>
          <div>
            <h1>Laporan Kersos</h1>
            <Subtitle>Kelola dan tinjau permintaan kersos</Subtitle>
          </div>
        </HeaderContent>
      </PageHeader>

      <DashboardGrid>
        <SideSection>
          <AdminSidebar user={adminData} accordionState={accordionState} setAccordionState={setAccordionState} />
        </SideSection>
        <MainSection>
          <ContentSection>
            <ToolbarSection>
              <FiltersContainer>
                <SearchContainer>
                  <SearchIcon>🔍</SearchIcon>
                  <SearchInput
                    type="text"
                    placeholder="Cari berdasarkan nama guru, siswa, atau mata pelajaran..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                </SearchContainer>
                <FilterGroup>
                  <FilterInput
                    type="date"
                    value={dateFilter}
                    onChange={(e) => setDateFilter(e.target.value)}
                    placeholder="Filter tanggal"
                  />
                </FilterGroup>
                <FilterGroup>
                  <FilterSelect
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">Semua Status</option>
                    <option value="menunggu">Menunggu</option>
                    <option value="disetujui">Disetujui</option>
                    <option value="ditolak">Ditolak</option>
                  </FilterSelect>
                </FilterGroup>
                <FilterGroup>
                  <ExportButton onClick={handleExcelExport}>
                    📊 Export Excel
                  </ExportButton>
                </FilterGroup>
              </FiltersContainer>
            </ToolbarSection>

            <TableContainer>
              <Table>
                <TableHeader>
                  <tr>
                    <th>Tanggal Kersos</th>
                    <th>Nama</th>
                    <th>Kelas</th>
                    <th>Jam Mulai</th>
                    <th>Jam Selesai</th>
                    <th>Jenis Pekerjaan</th>
                    <th>Status</th>
                    <th>Aksi</th>
                  </tr>
                </TableHeader>
                <TableBody>
                  {currentPageItems.length > 0 ? (
                    currentPageItems.map((request) => (
                      <TableRow
                        key={request.id}
                        onClick={() => setSelectedPermit(request)}
                        selected={selectedPermit?.id === request.id}
                      >
                        <td>{formatDate(request.tanggalKersos)}</td>
                        <td>
                          <StudentLink onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(request.student);
                          }}>
                            {request.nama}
                          </StudentLink>
                        </td>
                        <td>{request.kelas}</td>

                        <td>{request.jamMulai}</td>
                        <td>{request.jamSelesai}</td>
                        <td>{request.jenisPekerjaan}</td>

                        <td>
                          <StatusBadge status={request.status}>
                            {request.statusText}
                          </StatusBadge>
                        </td>
                        <td>
                          <ActionButtons>
                            <ActionButton onClick={(e) => {
                              e.stopPropagation();
                              setSelectedPermit(request);
                            }} title="Lihat Detail">
                              👁️
                            </ActionButton>
                          </ActionButtons>
                        </td>
                      </TableRow>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="8" style={{ textAlign: 'center', padding: '1rem' }}>
                        Tidak ada laporan
                      </td>
                    </tr>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            
            {/* Pagination Controls */}
            {totalItems > 0 && (
              <PaginationContainer>
                <PaginationInfo>
                  Menampilkan {startIndex + 1}-{endIndex} dari {totalItems} item
                </PaginationInfo>
                <PaginationControls>
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1 || loading}
                  >
                    ‹ Sebelumnya
                  </PaginationButton>
                  
                  <PageNumbers>
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = i + 1;
                      } else if (currentPage <= 3) {
                        pageNum = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + i;
                      } else {
                        pageNum = currentPage - 2 + i;
                      }
                      
                      return (
                        <PageNumber
                          key={pageNum}
                          onClick={() => setCurrentPage(pageNum)}
                          active={currentPage === pageNum}
                          disabled={loading}
                        >
                          {pageNum}
                        </PageNumber>
                      );
                    })}
                  </PageNumbers>
                  
                  <PaginationButton 
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages || loading}
                  >
                    Selanjutnya ›
                  </PaginationButton>
                </PaginationControls>
              </PaginationContainer>
            )}
          </ContentSection>
        </MainSection>
      </DashboardGrid>

      {selectedPermit && (
        <DetailModal onClick={() => setSelectedPermit(null)}>
          <DetailCard onClick={e => e.stopPropagation()}>
            <DetailHeader>
              <DetailHeaderContent>
                <h2>Detail Laporan Kersos</h2>
                <StatusContainer>
                  <DetailStatus status={selectedPermit.status}>
                    {selectedPermit.statusText}
                  </DetailStatus>
                  {selectedPermit.status !== 'pending' && selectedPermit.status !== 'menunggu' && (
                    <DecisionMaker>
                      Diputuskan oleh: {getDecisionMakerName(selectedPermit)}
                    </DecisionMaker>
                  )}
                </StatusContainer>
              </DetailHeaderContent>
              <CloseButton onClick={() => setSelectedPermit(null)}>×</CloseButton>
            </DetailHeader>

            <DetailContent>
              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Tanggal Kersos</DetailLabel>
                    <input
                      type="date"
                      value={selectedPermit.tanggalKersos || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, tanggalKersos: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Nama</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.nama || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, nama: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Kelas</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.kelas || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, kelas: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Nomor Telepon</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.nomorTelepon || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, nomorTelepon: e.target.value })}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              <DetailSection>
                <DetailGrid>
                  <DetailGroup>
                    <DetailLabel>Jam Mulai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamMulai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamMulai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>Jam Selesai</DetailLabel>
                    <input
                      type="time"
                      value={selectedPermit.jamSelesai || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jamSelesai: e.target.value })}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Jenis Pekerjaan</DetailLabel>
                    <textarea
                      value={selectedPermit.jenisPekerjaan || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, jenisPekerjaan: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup full>
                    <DetailLabel>Refleksi</DetailLabel>
                    <textarea
                      value={selectedPermit.refleksi || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, refleksi: e.target.value })}
                      style={{ width: '100%', minHeight: 60 }}
                    />
                  </DetailGroup>
                  <DetailGroup>
                    <DetailLabel>PIC</DetailLabel>
                    <input
                      type="text"
                      value={selectedPermit.pic || ''}
                      onChange={e => setSelectedPermit({ ...selectedPermit, pic: e.target.value })}
                    />
                  </DetailGroup>
                </DetailGrid>
              </DetailSection>

              {/* Images Section */}
              {selectedPermit.images && selectedPermit.images.length > 0 && (
                <DetailSection>
                  <DetailLabel>Lampiran Gambar</DetailLabel>
                  <ImagesGrid>
                    {selectedPermit.images.map((image, index) => {
                      // Handle different image formats similar to SicknessForm
                      let imgSrc = '';
                      if (typeof image === 'string') {
                        imgSrc = image;
                      } else if (image && image.image_url) {
                        // API returns image objects with image_url property
                        const baseUrl = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                        imgSrc = image.image_url.startsWith('http') 
                          ? image.image_url 
                          : `${baseUrl}/${image.image_url}`;
                      }
                      
                      return (
                        <ImageContainer key={index}>
                          <img
                            src={imgSrc}
                            alt={`Lampiran ${index + 1}`}
                            onClick={() => window.open(imgSrc, '_blank')}
                            onError={(e) => {
                              console.error('Image failed to load:', imgSrc);
                              e.target.style.display = 'none';
                            }}
                          />
                        </ImageContainer>
                      );
                    })}
                  </ImagesGrid>
                </DetailSection>
              )}

              <ActionButtons>
              <RejectButton 
                  type="button" 
                  onClick={async () => {
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'ditolak',
                        kersos_date: selectedPermit.tanggalKersos && selectedPermit.tanggalKersos.trim() !== '' ? selectedPermit.tanggalKersos : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        type_or_work: selectedPermit.jenisPekerjaan || null,
                        reflection: selectedPermit.refleksi || null,
                        pic: selectedPermit.pic || null,
                        pic_contact_number: selectedPermit.nomorTelepon || null
                      };
                      
                      console.log('Sending reject request data:', requestData);
                      console.log('Selected permit ID:', selectedPermit.id);
                      
                      const res = await authFetch(`${apiBase}/api/admin/kersos-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      console.log('Status updated successfully:', result);
                      
                      setNotification({
                        show: true,
                        message: 'Laporan kersos ditolak dan data diperbarui!',
                        type: 'info'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(searchQuery, dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Tolak
                </RejectButton>
                <ApproveButton 
                  type="button" 
                  onClick={async () => {
                    try {
                      const apiBase = (process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000').replace(/\/$/, '');
                      
                      const requestData = { 
                        status: 'disetujui',
                        kersos_date: selectedPermit.tanggalKersos && selectedPermit.tanggalKersos.trim() !== '' ? selectedPermit.tanggalKersos : null,
                        start_time: selectedPermit.jamMulai ? selectedPermit.jamMulai.substring(0, 5) : null, // Remove seconds
                        finish_time: selectedPermit.jamSelesai ? selectedPermit.jamSelesai.substring(0, 5) : null, // Remove seconds
                        type_or_work: selectedPermit.jenisPekerjaan || null,
                        reflection: selectedPermit.refleksi || null,
                        pic: selectedPermit.pic || null,
                        pic_contact_number: selectedPermit.nomorTelepon || null
                      };
                      
                      console.log('Sending request data:', requestData);
                      console.log('Selected permit ID:', selectedPermit.id);
                      
                      const res = await authFetch(`${apiBase}/api/admin/kersos-reports/${selectedPermit.id}/status`, {
                        method: 'PUT',
                        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                        body: JSON.stringify(requestData)
                      });
                      
                      if (!res.ok) {
                        const errorText = await res.text();
                        console.error('API Error Response:', errorText);
                        throw new Error(`Failed to update status: ${res.status} ${errorText}`);
                      }
                      
                      const result = await res.json();
                      console.log('Status updated successfully:', result);
                      
                      setNotification({
                        show: true,
                        message: 'Laporan kersos berhasil disetujui dan data diperbarui!',
                        type: 'success'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                        fetchReports(searchQuery, dateFilter, statusFilter);
                      }, 3000);
                    } catch (error) {
                      console.error('Error updating status:', error);
                      setNotification({
                        show: true,
                        message: `Error: ${error.message}`,
                        type: 'error'
                      });
                      
                      setTimeout(() => {
                        setNotification({ show: false, message: '', type: 'success' });
                      }, 5000);
                    }
                  }}
                >
                  Disetujui
                </ApproveButton>
                
              </ActionButtons>
            </DetailContent>
          </DetailCard>
        </DetailModal>
      )}

      {/* Notification Toast */}
      {notification.show && (
        <NotificationToast type={notification.type}>
          {notification.message}
        </NotificationToast>
      )}
    </PageContainer>
  );
};

// Reuse the same styled components from AdminKonselingPage
const PageContainer = styled.div`
  padding: 1.5rem;
  max-width: 1280px;
  margin: 0 auto;
  min-height: calc(100vh - 60px);
`;

const DashboardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 1.5rem;
  
  @media (max-width: 1100px) {
    grid-template-columns: 1fr;
  }
`;

const SideSection = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const MainSection = styled.div`
  display: flex;
  flex-direction: column;
`;

const PageHeader = styled.div`
  margin-bottom: 2rem;
`;

const HeaderContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const Subtitle = styled.p`
  color: var(--text-light);
  margin: 0.5rem 0 0 0;
  font-size: 0.875rem;
`;

const ContentSection = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  box-shadow: var(--box-shadow);
  overflow: hidden;
`;

const ToolbarSection = styled.div`
  padding: 1rem 1.5rem;
  border-bottom: 1px solid var(--border);
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  // max-width: 500px;
  background-color: var(--background-light);
  border-radius: 8px;
  padding: 0.5rem 1rem;
  flex: 1;
`;

const SearchIcon = styled.span`
  margin-right: 0.5rem;
  color: var(--text-light);
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: none;
  font-size: 0.875rem;
  color: var(--text-dark);
  outline: none;

  &::placeholder {
    color: var(--text-light);
  }
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background-color: var(--background-light);
  
  th {
    padding: 1rem;
    text-align: left;
    font-weight: 600;
    color: var(--text-dark);
    font-size: 0.875rem;
    border-bottom: 1px solid var(--border);
  }
`;

const TableBody = styled.tbody`
  tr:not(:last-child) {
    border-bottom: 1px solid var(--border);
  }
`;

const TableRow = styled.tr`
  cursor: pointer;
  background-color: ${props => props.selected ? 'var(--background-light)' : 'transparent'};
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }

  td {
    padding: 1rem;
    font-size: 0.875rem;
    color: var(--text-dark);
  }
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 50px;
  font-size: 0.75rem;
  font-weight: 600;
  background-color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#fff3e0';
      case 'approved':
      case 'disetujui': return '#e6f8f5';
      case 'rejected':
      case 'ditolak': return '#ffe6e6';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'pending':
      case 'menunggu': return '#e67700';
      case 'approved':
      case 'disetujui': return '#2ec27e';
      case 'rejected':
      case 'ditolak': return '#c01c28';
      default: return 'var(--text-dark)';
    }
  }};
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  justify-content: flex-start;
`;

const ActionButton = styled.button`
  background: none;
  border: none;
  font-size: 1rem;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  transition: background-color 0.2s;

  &:hover {
    background-color: var(--background-light);
  }
`;

const DetailModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 1rem;
  z-index: 1000;
`;

const DetailCard = styled.div`
  background-color: var(--background);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: var(--box-shadow);
  max-width: 600px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const DetailHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--border);
  
  h2 {
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--text-dark);
    margin: 0;
  }
`;

const DetailHeaderContent = styled.div`
  flex: 1;
  margin-right: 1rem;
`;

const DetailStatus = styled(StatusBadge)`
  font-size: 0.875rem;
`;

const StatusContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const DecisionMaker = styled.div`
  font-size: 0.75rem;
  color: var(--text-light);
  font-style: italic;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: var(--text-light);
  padding: 0.25rem;
  line-height: 1;
  
  &:hover {
    color: var(--text-dark);
  }
`;

const DetailContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const DetailSection = styled.div`
  margin-bottom: 2rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1.5rem;
  
  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const DetailGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  grid-column: ${props => props.full ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-light);
`;

const DetailValue = styled.div`
  font-size: 0.875rem;
  color: var(--text-dark);
`;

const ApproveButton = styled.button`
  flex: 1;
  background-color: var(--success, #2ec27e);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  font-size: 0.938rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  margin-top: 1.5rem;
  
  &:hover {
    background-color: #26a96b;
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
`;

const RejectButton = styled(ApproveButton)`
  background-color: var(--danger, #c01c28);
  
  &:hover {
    background-color: #a01622;
  }
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
  margin-top: 1rem;
`;

const ImageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 8px;
  cursor: pointer;
  min-height: 120px;
  max-height: 120px;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 8px;
  }
`;

const NotificationToast = styled.div`
  position: fixed;
  bottom: 20px;
  right: 20px;
  padding: 1rem;
  border-radius: 8px;
  background-color: ${props => props.type === 'success' ? 'var(--success, #2ec27e)' : 'var(--danger, #c01c28)'};
  color: white;
  font-weight: 500;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  animation: slideIn 0.3s ease-out forwards;
  max-width: 350px;
  
  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const StudentLink = styled.span`
  color: var(--primary, #3b82f6);
  cursor: pointer;
  text-decoration: none;

  &:hover {
    color: var(--primary-dark, #2563eb);
    text-decoration: underline;
  }
`;

const PaginationContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem 1.5rem;
  border-top: 1px solid var(--border);
  background-color: var(--background-light);
  flex-wrap: wrap;
  gap: 1rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    text-align: center;
  }
`;

const PaginationInfo = styled.div`
  font-size: 0.875rem;
  color: var(--text-light);
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PaginationButton = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: var(--background);
  color: var(--text-dark);
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 0.25rem;
  flex-wrap: wrap;
`;

const PageNumber = styled.button`
  padding: 0.5rem 0.75rem;
  border: 1px solid var(--border);
  background-color: ${props => props.active ? 'var(--primary)' : 'var(--background)'};
  color: ${props => props.active ? 'white' : 'var(--text-dark)'};
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.875rem;
  transition: all 0.2s;
  min-width: 40px;

  &:hover {
    background-color: var(--primary);
    color: white;
    border-color: var(--primary);
  }
`;

const FiltersContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const FilterGroup = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const FilterInput = styled.input`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  outline: none;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--primary);
  }

  &::placeholder {
    color: var(--text-light);
  }
`;

const FilterSelect = styled.select`
  padding: 0.5rem;
  border: 1px solid var(--border);
  border-radius: 6px;
  font-size: 0.875rem;
  background-color: var(--background);
  color: var(--text-dark);
  outline: none;
  cursor: pointer;
  transition: border-color 0.2s;

  &:focus {
    border-color: var(--primary);
  }
`;

const ExportButton = styled.button`
  background-color: var(--primary, #3b82f6);
  color: white;
  border: none;
  border-radius: 6px;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 140px;
  justify-content: center;

  &:hover {
    background-color: var(--primary-dark, #2563eb);
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  }

  &:active {
    transform: translateY(0);
  }
`;

export default AdminLaporanKersosPage;